import { useState } from 'react';
import { useEarlyAccessLeads } from '../../hooks/useAdminOps';
import type {
  EarlyAccessLeadItem,
  WebsiteLeadAccountFilter,
  WebsiteLeadConsentFilter,
  WebsiteLeadOutreachFilter,
  WebsiteLeadSourceFilter,
} from '../../api/adminOps';
import { QueueTable, StatusChip, DetailDrawer, type QueueColumn } from '../../components/ui';
import {
  ACCOUNT_FILTERS,
  CONSENT_FILTERS,
  OUTREACH_FILTERS,
  SEARCH_DEBOUNCE_MS,
  accountChip,
  consentChip,
  donationFrequencyLabel,
  earlyAccessTiles,
  eligibilityChip,
  fullName,
  isRepeatSubmission,
  lifecycleLabel,
  referralSourceLabel,
  sourceLabel,
} from '../websiteLeads.logic';
import {
  AccountAndContact,
  ChipFilterGroup,
  ConsentDetail,
  DetailField,
  DetailSection,
  LoadError,
  MailLink,
  OutreachCell,
  SearchAndSource,
  absolute,
  relative,
  useDebouncedValue,
} from './parts';
import OutreachSection from './OutreachSection';
import OutreachTools from './OutreachTools';

/**
 * DM-7703: the Early Access waitlist, read only.
 * DM-8485: plus the outreach log behind each lead and the mail-merge sheet
 * round trip for operators.
 */
export default function EarlyAccessTab() {
  const [consent, setConsent] = useState<WebsiteLeadConsentFilter>('all');
  const [account, setAccount] = useState<WebsiteLeadAccountFilter>('all');
  const [outreach, setOutreach] = useState<WebsiteLeadOutreachFilter>('all');
  const [source, setSource] = useState<WebsiteLeadSourceFilter>('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);

  // The list and the export share these filters, so what is exported is what is shown.
  const filters = {
    consent,
    account,
    outreach,
    source,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  };
  const queue = useEarlyAccessLeads(filters);
  const selected = queue.rows.find(row => row.id === selectedId) ?? null;

  const columns: QueueColumn<EarlyAccessLeadItem>[] = [
    {
      key: 'joined',
      header: 'Joined',
      render: row => (
        <span className="text-gray-text whitespace-nowrap">{relative(row.first_submitted_at)}</span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: row => <span className="text-white">{fullName(row)}</span>,
    },
    {
      key: 'email',
      header: 'Email',
      render: row => <span className="text-gray-text break-all">{row.email}</span>,
    },
    {
      key: 'frequency',
      header: 'Gives',
      render: row => (
        <span className="text-gray-text">
          {donationFrequencyLabel(row.donation_frequency) ?? '—'}
        </span>
      ),
    },
    {
      key: 'consent',
      header: 'Updates',
      render: row => <StatusChip {...consentChip(row.marketing_consent)} />,
    },
    {
      key: 'account',
      header: 'Account',
      render: row => <StatusChip {...accountChip(row)} />,
    },
    {
      key: 'contact',
      header: 'Contact',
      render: row => <StatusChip {...eligibilityChip(row.contact_eligibility)} />,
    },
    {
      key: 'outreach',
      header: 'Outreach',
      render: row => <OutreachCell summary={row.outreach} />,
    },
    {
      key: 'source',
      header: 'Source',
      render: row => <span className="text-gray-text">{sourceLabel(row.source)}</span>,
    },
    {
      key: 'activity',
      header: 'Last activity',
      render: row => (
        <div className="flex items-center gap-2 whitespace-nowrap">
          <span className="text-gray-text">{relative(row.last_submitted_at)}</span>
          {isRepeatSubmission(row) && <StatusChip label="Repeat" tone="info" />}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {queue.summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {earlyAccessTiles(queue.summary).map(tile => (
            <div key={tile.label} className="glass-card p-4">
              <p className="text-xs text-gray-text uppercase tracking-wide">{tile.label}</p>
              <p className="text-2xl font-semibold text-white mt-1">{tile.value}</p>
              {tile.hint && <p className="text-xs text-gray-text mt-1">{tile.hint}</p>}
            </div>
          ))}
        </div>
      )}

      <div className="glass-card p-4 space-y-3">
        <ChipFilterGroup
          label="Updates"
          options={CONSENT_FILTERS}
          value={consent}
          onChange={value => {
            setConsent(value);
            setSelectedId(null);
          }}
        />
        <ChipFilterGroup
          label="Account"
          options={ACCOUNT_FILTERS}
          value={account}
          onChange={value => {
            setAccount(value);
            setSelectedId(null);
          }}
        />
        <ChipFilterGroup
          label="Outreach"
          options={OUTREACH_FILTERS}
          value={outreach}
          onChange={value => {
            setOutreach(value);
            setSelectedId(null);
          }}
        />
        <SearchAndSource
          idPrefix="early-access"
          search={search}
          onSearch={setSearch}
          source={source}
          onSource={setSource}
          placeholder="Search name or email"
        />
        <OutreachTools leadType="early_access" params={filters} />
      </div>

      {queue.isError ? (
        <LoadError what="Early Access leads" error={queue.error} retry={() => queue.refetch()} />
      ) : (
        <QueueTable
          columns={columns}
          rows={queue.rows}
          rowKey={row => row.id}
          isLoading={queue.isPending}
          onRowClick={row => setSelectedId(row.id)}
          rowActionLabel={row => `Open ${fullName(row)}`}
          emptyTitle="No Early Access leads"
          emptyHint={
            debouncedSearch
              ? `Nothing matches "${debouncedSearch}".`
              : 'No lead matches these filters.'
          }
          hasNextPage={queue.hasNextPage}
          isFetchingNextPage={queue.isFetchingNextPage}
          onLoadMore={() => queue.fetchNextPage()}
        />
      )}

      <DetailDrawer
        open={selected !== null}
        title={selected ? fullName(selected) : 'Early Access lead'}
        onClose={() => setSelectedId(null)}
      >
        {selected && (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <StatusChip {...consentChip(selected.marketing_consent)} />
              <StatusChip {...accountChip(selected)} />
              <StatusChip label={sourceLabel(selected.source)} tone="neutral" />
              {isRepeatSubmission(selected) && <StatusChip label="Repeat signup" tone="info" />}
            </div>
            <AccountAndContact lead={selected} />
            <OutreachSection leadType="early_access" lead={selected} />
            <DetailSection title="Contact">
              <DetailField label="Name" value={fullName(selected)} />
              <DetailField label="Email" value={<MailLink email={selected.email} />} />
              <DetailField
                label="Gives"
                value={donationFrequencyLabel(selected.donation_frequency)}
              />
            </DetailSection>
            <DetailSection title="Waitlist">
              <DetailField label="Joined" value={absolute(selected.first_submitted_at)} />
              <DetailField label="Last submitted" value={absolute(selected.last_submitted_at)} />
              <DetailField label="Status" value={lifecycleLabel(selected.lifecycle_status)} />
              <DetailField
                label="Launch email"
                value={selected.launch_notification_expected ? 'Expected' : 'Not expected'}
              />
            </DetailSection>
            <DetailSection title="Attribution & consent">
              <ConsentDetail consent={selected.marketing_consent} />
              <DetailField
                label="Heard about us"
                value={referralSourceLabel(selected.referral_source)}
              />
              <DetailField label="Referral code" value={selected.referral_code} />
              <DetailField label="Submitted from" value={selected.source_path} />
              <DetailField label="Form version" value={selected.form_version} />
            </DetailSection>
          </>
        )}
      </DetailDrawer>
    </div>
  );
}
